// Moved these here to prevent vites fast refresh from always refreshing teh entire page while im trying to work on the assetdetailmodal
import { DateTime } from 'luxon'

import type { AssetState } from '@/types/asset-types'
import { celsiusToUser } from '@/utils/conversions'
const bearings = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export const REEFER_INTERFACE = { FULL: 1, NONE: 2, NO_REEFER: 3 } as const

export const VSP_ACCESS_TYPE = {
  DISABLED: 1,
  NO_REEFER_INTERFACE_ONLY: 2,
  REEFER_AND_REEFER_INTERFACE: 3,
} as const

export interface VisibleSections {
  zones: boolean
  monitoringPoints: boolean
  reefer: boolean
  reeferMakeModelOnly: boolean
  engineAndMaintenance: boolean
  doors: boolean
}

export const bearingDirection = (bearing: string) => {
  const bearingDegrees = Number(bearing)
  if (typeof bearingDegrees === 'number' && bearingDegrees >= 0 && bearingDegrees <= 360) {
    const quadrant = Math.floor((bearingDegrees + 22.5) / 45)

    if (quadrant === 8) {
      return bearings[0]
    } else if (quadrant >= 0 && quadrant < 8) {
      return bearings[quadrant]
    }
  }
}

export const mapPowerSource = (ps: string) => {
  if (ps === 'D') {
    return 'Diesel'
  }
  if (ps == 'E') {
    return 'Electric'
  }
  return 'N/A'
}

export const mapAirControl = (ac: string) => {
  if (ac === 'R') {
    return 'Return Air'
  }
  if (ac == 'S') {
    return 'Supply Air'
  }
  return 'N/A'
}

export const mapEngineSpeed = (es: string) => {
  if (es === 'L') {
    return 'Low'
  }
  if (es == 'H') {
    return 'High'
  }
  return 'N/A'
}

export const formatDate = (isoDate?: string): string => {
  return isoDate ? DateTime.fromISO(isoDate).toLocaleString(DateTime.DATETIME_MED) : 'N/A'
}

export const formatNumber = (value: number | null | undefined, decimals: number, suffix?: string) => {
  if (!value) {
    return 'N/A'
  }
  return `${value.toFixed(decimals)}${suffix ?? ''}`
}

export const convertTemp = (temp: number, units: 'Metric' | 'Imperial') => {
  if (!temp || typeof temp !== 'number') {
    return 'N/A'
  }
  const userTemp = celsiusToUser(temp, 1, units)
  const degree = units === 'Metric' ? 'C' : 'F'
  return `${userTemp} °${degree}`
}

/** A zone table cell: blank when the reefer reported no value, so 0 °C still shows. */
export const formatZoneTemp = (temp: number | null, units: 'Metric' | 'Imperial') => {
  if (temp === null) {
    return ''
  }
  const degree = units === 'Metric' ? 'C' : 'F'
  return `${celsiusToUser(temp, 1, units)} °${degree}`
}

export const getVisibleSections = (
  asset: Pick<AssetState, 'doors' | 'monitoringPoints'> & { reefer: Pick<AssetState['reefer'], 'interfaceTypeId'> }
): VisibleSections => {
  const type = asset.reefer.interfaceTypeId
  const hasDoors = asset.doors.length > 0

  if (type === REEFER_INTERFACE.NO_REEFER) {
    return {
      zones: false,
      monitoringPoints: false,
      reefer: false,
      reeferMakeModelOnly: false,
      engineAndMaintenance: false,
      doors: hasDoors,
    }
  }
  if (type === REEFER_INTERFACE.NONE) {
    return {
      zones: false,
      monitoringPoints: asset.monitoringPoints.length > 0,
      reefer: true,
      reeferMakeModelOnly: true,
      engineAndMaintenance: false,
      doors: hasDoors,
    }
  }

  return {
    zones: true,
    monitoringPoints: true,
    reefer: true,
    reeferMakeModelOnly: false,
    engineAndMaintenance: true,
    doors: hasDoors,
  }
}

/**
 * Checks whether virtual set point is enabled for the asset.
 */
export const isVirtualSetPointEnabledForAsset = (
  virtualSetpointAccessTypeId?: number,
  reeferInterfaceType?: number | null
): boolean => {
  // NO_REEFER_INTERFACE_ONLY AND REEFER_INTERFACE.NONE -> VSP enabled for the asset
  // REEFER_AND_REEFER_INTERFACE AND REEFER_INTERFACE.FULL -> VSP enabled for the asset
  // REEFER_AND_REEFER_INTERFACE AND REEFER_INTERFACE.NONE -> VSP enabled for the asset
  // DISABLED -> VSP disabled for the asset

  switch (virtualSetpointAccessTypeId) {
    case VSP_ACCESS_TYPE.NO_REEFER_INTERFACE_ONLY:
      return reeferInterfaceType === REEFER_INTERFACE.NONE
    case VSP_ACCESS_TYPE.REEFER_AND_REEFER_INTERFACE:
      return reeferInterfaceType !== REEFER_INTERFACE.NO_REEFER
    case VSP_ACCESS_TYPE.DISABLED:
    default:
      return false
  }
}
